import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ButtonBase from '@mui/material/ButtonBase'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { alpha, useTheme } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

/**
 * Height of the persistent storefront bottom nav (the raised centre button overflows above it and
 * is visual only). Pages/fixed bars offset by this so nothing hides behind the bar.
 */
export const BOTTOM_NAV_HEIGHT = 62

type Item = { key: string; to: string; icon: ReactNode; active: boolean }

/**
 * Always-on storefront bottom navigation: Your Orders · Home · Profile. Fixed to the viewport
 * bottom on every storefront page. The centre Home button is raised into a brand-tinted circle
 * (matches every other CTA — the shop's brand_color, not a hardcoded colour, keeps it white-label).
 * "Your Orders" / "Profile" route to the account area; a guest tapping either lands on /login
 * (the /account guard redirects), which is the intended sign-in prompt.
 */
export default function BottomNav() {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const items: Item[] = [
    { key: 'orders', to: '/account', icon: <ReceiptLongOutlinedIcon />, active: pathname === '/account' },
    { key: 'home', to: '/', icon: <HomeRoundedIcon />, active: pathname === '/' },
    { key: 'profile', to: '/account/profile', icon: <PersonOutlineIcon />, active: pathname === '/account/profile' },
  ]

  return (
    <Paper
      component="nav"
      elevation={0}
      square
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (th) => th.zIndex.appBar,
        height: BOTTOM_NAV_HEIGHT,
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `1px solid ${theme.palette.divider}`,
        // Sit above the safe-area inset on notched phones without changing the visual height.
        pb: 'env(safe-area-inset-bottom)',
        bgcolor: 'background.paper',
      }}
    >
      {items.map((item) =>
        item.key === 'home' ? (
          <HomeButton
            key={item.key}
            label={t('storefront.nav.home')}
            active={item.active}
            onClick={() => navigate(item.to)}
          >
            {item.icon}
          </HomeButton>
        ) : (
          <ButtonBase
            key={item.key}
            onClick={() => navigate(item.to)}
            aria-label={t(`storefront.nav.${item.key}`)}
            aria-current={item.active ? 'page' : undefined}
            sx={{
              flex: 1,
              flexDirection: 'column',
              gap: 0.25,
              color: item.active ? 'primary.main' : 'text.secondary',
              transition: 'color .15s',
            }}
          >
            {item.icon}
            <Typography variant="caption" sx={{ fontWeight: item.active ? 700 : 600 }}>
              {t(`storefront.nav.${item.key}`)}
            </Typography>
          </ButtonBase>
        ),
      )}
    </Paper>
  )
}

/** The raised centre Home button — a brand-gradient circle lifted above the bar. */
function HomeButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  const theme = useTheme()
  const primary = theme.palette.primary.main
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      sx={{ flex: 1, flexDirection: 'column', gap: 0.25 }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mt: '-22px', // lift the circle above the bar's top edge
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.primary.contrastText,
          backgroundImage: `linear-gradient(140deg, ${theme.palette.secondary.main}, ${primary})`,
          border: `3px solid ${theme.palette.background.paper}`,
          boxShadow: `0 6px 16px ${alpha(primary, 0.45)}`,
          '& svg': { fontSize: 28 },
        }}
      >
        {children}
      </Box>
      <Typography variant="caption" sx={{ mt: '-2px', fontWeight: 700, color: 'primary.main' }}>
        {label}
      </Typography>
    </ButtonBase>
  )
}
